import React from 'react';
import { motion } from 'framer-motion';
import { FaCheck } from 'react-icons/fa';
import usePageTitle from '../hooks/usePageTitle';
import ClientsShowcase from '../components/ClientsShowcase';
import './About.css';

const About = () => {
  usePageTitle('About Us');

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <motion.div 
            className="about-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>About House of Canvas</h1>
            <p>Your Top All-Weather Tent Partner in East Africa</p>
          </motion.div>
        </div>
      </section>
      
      {/* Company Overview */}
      <section className="company-overview">
        <div className="container">
          <div className="overview-grid">
            <motion.div 
              className="overview-content"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Our Story</h2>
              <p>
                House of Canvas was founded in 2010 with a vision to provide high-quality, durable, and innovative tent solutions across East Africa. 
                What started as a small workshop has now grown into a leading tent manufacturer serving clients across Tanzania, Kenya, and beyond.
              </p>
              <p>
                With more than 10 years of industry expertise as pioneers in stretch tents, we offer prompt delivery, efficient installation services, and customized structures. 
                We understand the unique demands of the industry and supply accordingly.
              </p>
              <p>
                Our team is dedicated to delivering high-quality products and services that our clients can rely on. 
                We are recognized for our leadership in the industry and our reputation is built on trust and uncompromising quality.
              </p>
            </motion.div>
            <motion.div 
              className="overview-image"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img src={process.env.PUBLIC_URL + 'https://house-of-canvas.vercel.app/images/about-hero.jpg'} alt="House of Canvas workshop" />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Mission & Vision */}
      <section className="mission-vision">
        <div className="container">
          <div className="mission-vision-grid">
            <motion.div 
              className="mission-box"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3>Our Mission</h3>
              <p>
                To provide innovative, high-quality tent solutions that exceed customer expectations while maintaining the highest standards of craftsmanship, service, and environmental responsibility.
              </p>
            </motion.div>
            <motion.div 
              className="vision-box"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>Our Vision</h3>
              <p>
                To be the leading tent manufacturer in East Africa, recognized for our innovation, quality, and commitment to customer satisfaction, while expanding our reach to serve clients across the continent.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Values */}
      <section className="values-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Our Core Values</h2>
            <p>The principles that guide everything we do</p>
          </motion.div>
          
          <div className="values-grid">
            {[
              { title: 'Quality', description: 'We are committed to excellence in every tent we produce, using only the finest materials and craftsmanship.' },
              { title: 'Innovation', description: 'We continuously seek new ways to improve our products and processes to better serve our customers.' },
              { title: 'Integrity', description: 'We conduct our business with honesty, transparency, and ethical practices.' },
              { title: 'Customer Focus', description: 'We prioritize understanding and meeting the unique needs of each client.' },
              { title: 'Teamwork', description: 'We value collaboration and the diverse skills and perspectives of our team members.' },
              { title: 'Sustainability', description: 'We are committed to environmentally responsible practices in our operations.' }
            ].map((value, index) => (
              <motion.div 
                key={index} 
                className="value-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Why Choose Us */}
      <section className="why-choose-section">
        <div className="container">
          <div className="why-choose-grid">
            <motion.div 
              className="why-choose-content"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose House of Canvas?</h2>
              <ul className="benefits-list">
                <li><FaCheck className="check-icon" /> Over 10 years of industry expertise</li>
                <li><FaCheck className="check-icon" /> Custom-designed tents to meet your specific needs</li>
                <li><FaCheck className="check-icon" /> High-quality, durable materials that withstand all weather conditions</li>
                <li><FaCheck className="check-icon" /> Professional installation and maintenance services</li>
                <li><FaCheck className="check-icon" /> Competitive pricing without compromising on quality</li>
                <li><FaCheck className="check-icon" /> Excellent customer service and after-sales support</li>
                <li><FaCheck className="check-icon" /> Fast turnaround times and reliable delivery</li>
                <li><FaCheck className="check-icon" /> Environmentally responsible manufacturing practices</li>
              </ul>
            </motion.div>
            <motion.div 
              className="why-choose-image"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img src="/images/companyImage/488908873.jpg" alt="House of Canvas quality tents" />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="about-cta-section">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Ready to start your tent project?</h2>
            <p>Contact us today to discuss your requirements and get a free consultation.</p>
            <a href="/contact" className="cta-button">Get in Touch</a>
          </motion.div>
        </div>
      </section>

      {/* Our Journey Timeline */}
      <section className="journey-section">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Our Journey</h2>
            <p>From humble beginnings to becoming East Africa's premier tent provider</p>
          </motion.div>
          
          <div className="timeline">
            <motion.div 
              className="timeline-item"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="timeline-content">
                <div className="timeline-year">2010</div>
                <h3>Our Foundation</h3>
                <p>House of Canvas was established in Tanzania, focusing on quality tent manufacturing for the local market.</p>
              </div>
              <div className="timeline-image">
                <img src="/images/companyImage/2A5A7034.JPG" alt="Company founding" />
              </div>
            </motion.div>
            
            <motion.div 
              className="timeline-item reverse"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="timeline-content">
                <div className="timeline-year">2013</div>
                <h3>Expansion to Kenya</h3>
                <p>We expanded our operations to Kenya, bringing our quality tent solutions to a wider East African market.</p>
              </div>
              <div className="timeline-image">
                <img src="/images/companyImage/2A5A7088.JPG" alt="Kenya expansion" />
              </div>
            </motion.div>
            
            <motion.div 
              className="timeline-item"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="timeline-content">
                <div className="timeline-year">2016</div>
                <h3>Innovation in Design</h3>
                <p>We introduced innovative stretch tent designs, revolutionizing the event industry across East Africa.</p>
              </div>
              <div className="timeline-image">
                <img src="/images/companyImage/2A5A7126.JPG" alt="Innovative designs" />
              </div>
            </motion.div>
            
            <motion.div 
              className="timeline-item reverse"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="timeline-content">
                <div className="timeline-year">2020</div>
                <h3>Sustainable Practices</h3>
                <p>We implemented eco-friendly manufacturing processes and materials, committing to sustainable business practices.</p>
              </div>
              <div className="timeline-image">
                <img src="/images/companyImage/2A5A7459.JPG" alt="Sustainable practices" />
              </div>
            </motion.div>
            
            <motion.div 
              className="timeline-item"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="timeline-content">
                <div className="timeline-year">Today</div>
                <h3>Leading the Industry</h3>
                <p>Today, we're proud to be East Africa's premier tent provider, serving clients across multiple industries with innovative solutions.</p>
              </div>
              <div className="timeline-image">
                <img src="/images/companyImage/2A5A7489.JPG" alt="Industry leader" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Facilities */}
      <section className="facilities-section">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Our Facilities</h2>
            <p>State-of-the-art manufacturing and design centers</p>
          </motion.div>
          
          <div className="facilities-grid">
            <motion.div 
              className="facility-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)" }}
            >
              <div className="facility-image">
                <img src="/images/companyImage/angata-ngorongoro-camp-1920x1280-11-1240x780.jpg" alt="Manufacturing facility" />
              </div>
              <div className="facility-content">
                <h3>Manufacturing Center</h3>
                <p>Our 5,000 square meter manufacturing facility in Arusha is equipped with cutting-edge technology for precision tent production.</p>
              </div>
            </motion.div>
            
            <motion.div 
              className="facility-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)" }}
            >
              <div className="facility-image">
                <img src="/images/Capture3.PNG" alt="Design studio" />
              </div>
              <div className="facility-content">
                <h3>Design Studio</h3>
                <p>Our design studio brings creative concepts to life, where our team of designers work on innovative tent solutions.</p>
              </div>
            </motion.div>
            
            <motion.div 
              className="facility-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)" }}
            >
              <div className="facility-image">
                <img src="/images/3.jpg" alt="Quality control" />
              </div>
              <div className="facility-content">
                <h3>Quality Control</h3>
                <p>Our dedicated quality control department ensures that every tent meets our rigorous standards before delivery.</p>
              </div>
            </motion.div>
            
            <motion.div 
              className="facility-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)" }}
            >
              <div className="facility-image">
                <img src="/images/team-hero.png" alt="Showroom" />
              </div>
              <div className="facility-content">
                <h3>Showroom</h3>
                <p>Visit our showroom to experience our tent solutions firsthand and discuss your specific requirements with our experts.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Add Clients Showcase Section */}
      <section className="about-clients">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Our Valued Partners</h2>
            <p>Trusted by leading organizations across East Africa</p>
          </motion.div>
          
          <div className="clients-section">
          <h2>JOIN OUR SATISFIED CLIENTS</h2>
          <p>As East Africa's Leading Premier Tent Manufacturer<br />We are proud to have earned the trust of these top brands and companies.</p>
          
          <div className="client-logos">
            <img src="/images/partiners/myo.svg" alt="Moyo" />
            <img src="/images/partiners/singita.svg" alt="Singita" />
            <img src="/images/partiners/malaika.svg" alt="Malaika Camps & Lodges" />
            <img src="/images/clients/legendary.svg" alt="client Wetu" />
            <img src="/images/partiners/asilia.svg" alt="Asilia" />
            <img src="/images/partiners/tato.svg" alt="Tato" />
            <img src="/images/partiners/mgunga.svg" alt="Mgunga" />
            <img src="/images/partiners/kifaru.svg" alt="Kifaru" />
          </div>
        </div>
        </div>
      </section>


      {/* Tutarudisha Wakitakaa.... */}

      {/* Testimonials Section */}
      {/* <section className="about-testimonials">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>What Our Clients Say</h2>
            <p>Trusted by businesses and individuals across East Africa</p>
          </motion.div>
          
          <div className="testimonials-grid">
            <motion.div 
              className="testimonial-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="testimonial-content">
                <div className="quote-icon">❝</div>
                <p>"House of Canvas provided exceptional tents for our safari camp. Their attention to detail and quality craftsmanship has significantly enhanced our guests' experience."</p>
                <div className="testimonial-author">
                  <img src="/images/companyImage/client1.jpg" alt="Sarah Johnson" />
                  <div>
                    <h4>Sarah Johnson</h4>
                    <p>Safari Camp Manager, Serengeti Wilderness Camps</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="testimonial-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="testimonial-content">
                <div className="quote-icon">❝</div>
                <p>"We've been using House of Canvas tents for our corporate events for years. Their products are reliable, stylish, and always impress our clients. Highly recommended!"</p>
                <div className="testimonial-author">
                  <img src="/images/companyImage/client2.jpg" alt="Michael Omondi" />
                  <div>
                    <h4>Michael Omondi</h4>
                    <p>Events Director, Nairobi Corporate Solutions</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="testimonial-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="testimonial-content">
                <div className="quote-icon">❝</div>
                <p>"The custom wedding tent created by House of Canvas was absolutely stunning. They transformed our vision into reality and made our special day even more magical."</p>
                <div className="testimonial-author">
                  <img src="/images/companyImage/client3.jpg" alt="Amina and David" />
                  <div>
                    <h4>Amina and David</h4>
                    <p>Wedding Clients, Dar es Salaam</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default About; 